/**
 * BehaviorTracker — Client-side behavioral signal collector
 *
 * Attaches to the DOM and tracks implicit user behavior:
 * - Scroll depth (%)
 * - Time on page (seconds)
 * - Click count
 * - CTA clicks
 * - Idle detection (30s+ no activity)
 * - Exit intent (mouse leaves viewport / tab hidden)
 * - Form field timing (focus/blur)
 * - Backtracking (going back in form steps)
 *
 * Usage:
 *   const tracker = new BehaviorTracker(sessionId, supabaseUrl, anonKey);
 *   tracker.start();
 *   // ... later ...
 *   const snapshot = tracker.getSnapshot();
 *   tracker.destroy();
 */

import type { BehaviorData } from '@/engine/IntentScorer';

interface BehaviorEvent {
  session_id: string;
  event_type: string;
  timestamp: number;
  payload: Record<string, unknown>;
}

export class BehaviorTracker {
  private sessionId: string;
  private supabaseUrl: string;
  private anonKey: string;
  private startTime: number;
  private lastActivityTime: number;
  private maxScrollDepth = 0;
  private clickCount = 0;
  private ctaClicks = 0;
  private formStarted = false;
  private formCompleted = false;
  private highIntentFieldsCompleted = false;
  private meaningfulInteractions = 0;
  private totalIdleTime = 0;
  private exitIntentTriggered = false;
  private exitIntentBeforeEngagement = false;
  private hesitationEvents = 0;
  private backtrackEvents = 0;
  private fieldEdits = 0;
  private currentStep = 0;
  private eventBuffer: BehaviorEvent[] = [];
  private flushInterval: ReturnType<typeof setInterval> | null = null;
  private idleCheckInterval: ReturnType<typeof setInterval> | null = null;
  private destroyed = false;

  // Bound handlers for cleanup
  private handleScroll: () => void;
  private handleClick: (e: MouseEvent) => void;
  private handleMouseLeave: (e: MouseEvent) => void;
  private handleVisibilityChange: () => void;
  private handleActivity: () => void;

  constructor(sessionId: string, supabaseUrl: string, anonKey: string) {
    this.sessionId = sessionId;
    this.supabaseUrl = supabaseUrl;
    this.anonKey = anonKey;
    this.startTime = Date.now();
    this.lastActivityTime = Date.now();

    // Bind handlers
    this.handleScroll = this._onScroll.bind(this);
    this.handleClick = this._onClick.bind(this);
    this.handleMouseLeave = this._onMouseLeave.bind(this);
    this.handleVisibilityChange = this._onVisibilityChange.bind(this);
    this.handleActivity = this._onActivity.bind(this);
  }

  start(): void {
    // Track page view
    this.pushEvent('page_view', { url: window.location.href, referrer: document.referrer });

    // Scroll tracking
    window.addEventListener('scroll', this.handleScroll, { passive: true });

    // Click tracking
    document.addEventListener('click', this.handleClick);

    // Exit intent (desktop: mouse leaving viewport)
    document.addEventListener('mouseleave', this.handleMouseLeave);

    // Exit intent (mobile/tab switch)
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    // Activity tracking for idle detection
    document.addEventListener('mousemove', this.handleActivity, { passive: true });
    document.addEventListener('touchstart', this.handleActivity, { passive: true });
    document.addEventListener('keydown', this.handleActivity, { passive: true });

    // Flush event buffer every 3 seconds
    this.flushInterval = setInterval(() => this.flush(), 3000);

    // Idle check every 5 seconds
    this.idleCheckInterval = setInterval(() => this._checkIdle(), 5000);
  }

  destroy(): void {
    this.destroyed = true;
    this.flush(); // Final flush

    window.removeEventListener('scroll', this.handleScroll);
    document.removeEventListener('click', this.handleClick);
    document.removeEventListener('mouseleave', this.handleMouseLeave);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    document.removeEventListener('mousemove', this.handleActivity);
    document.removeEventListener('touchstart', this.handleActivity);
    document.removeEventListener('keydown', this.handleActivity);

    if (this.flushInterval) clearInterval(this.flushInterval);
    if (this.idleCheckInterval) clearInterval(this.idleCheckInterval);
  }

  /** Call when user starts filling form (first question) */
  trackFormStart(): void {
    if (!this.formStarted) {
      this.formStarted = true;
      this.pushEvent('form_start', {});
    }
  }

  /** Call when user completes entire form */
  trackFormComplete(): void {
    this.formCompleted = true;
    this.pushEvent('form_complete', {});
  }

  /** Call when user provides email or phone */
  trackHighIntentField(field: string): void {
    this.highIntentFieldsCompleted = true;
    this.meaningfulInteractions++;
    this.pushEvent('field_complete', { field_name: field, high_intent: true });
  }

  /** Call when user completes a field */
  trackFieldComplete(fieldName: string, timeSpentMs: number): void {
    this.meaningfulInteractions++;
    if (timeSpentMs > 10000) this.hesitationEvents++; // >10s on one field = hesitation
    this.pushEvent('field_complete', { field_name: fieldName, time_spent_ms: timeSpentMs });
  }

  /** Call when user edits a previously completed field */
  trackFieldEdit(fieldName: string): void {
    this.fieldEdits++;
    this.pushEvent('field_edit', { field_name: fieldName, edit_count: this.fieldEdits });
  }

  /** Call when user clicks a CTA button */
  trackCtaClick(ctaId: string): void {
    this.ctaClicks++;
    this.meaningfulInteractions++;
    this.pushEvent('cta_click', { cta_id: ctaId });
  }

  /** Call when user goes back a step in profiling */
  trackBacktrack(fromStep: number, toStep: number): void {
    this.backtrackEvents++;
    this.pushEvent('backtrack', { from_step: fromStep, to_step: toStep });
  }

  /** Call when ProfileBuilder step changes */
  trackStepChange(step: number): void {
    if (step < this.currentStep) {
      this.trackBacktrack(this.currentStep, step);
    }
    this.currentStep = step;
  }

  /** Get current behavioral snapshot for scoring */
  getSnapshot(): BehaviorData {
    const now = Date.now();
    const sessionDuration = (now - this.startTime) / 1000;
    const timeSinceActivity = (now - this.lastActivityTime) / 1000;

    return {
      time_on_page_seconds: Math.round(sessionDuration),
      scroll_depth_percent: this.maxScrollDepth,
      click_count: this.clickCount,
      cta_clicks: this.ctaClicks,
      form_started: this.formStarted,
      form_completed: this.formCompleted,
      high_intent_fields_completed: this.highIntentFieldsCompleted,
      meaningful_interactions: this.meaningfulInteractions,
      idle_time_seconds: Math.round(this.totalIdleTime + (timeSinceActivity > 30 ? timeSinceActivity : 0)),
      exit_intent_before_engagement: this.exitIntentBeforeEngagement,
      bounce_signal: sessionDuration < 5 && this.clickCount === 0 && !this.formStarted,
      hesitation_events: this.hesitationEvents,
      backtrack_events: this.backtrackEvents,
      session_duration_seconds: Math.round(sessionDuration),
      field_edits: this.fieldEdits,
    };
  }

  // ─── Private Methods ──────────────────────────────────

  private _onScroll(): void {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (docHeight <= 0) return;
    const depth = Math.round((scrollTop / docHeight) * 100);
    if (depth > this.maxScrollDepth) {
      this.maxScrollDepth = depth;
      // Log at milestones
      if ([25, 50, 70, 90, 100].includes(depth)) {
        this.pushEvent('scroll', { depth_percent: depth });
      }
    }
    this._onActivity();
  }

  private _onClick(e: MouseEvent): void {
    this.clickCount++;
    this._onActivity();
  }

  private _onMouseLeave(e: MouseEvent): void {
    if (e.clientY <= 0) {
      this.exitIntentTriggered = true;
      if (this.clickCount === 0 && !this.formStarted) {
        this.exitIntentBeforeEngagement = true;
      }
      this.pushEvent('exit_intent', { trigger: 'mouse_leave', engaged: this.formStarted });
    }
  }

  private _onVisibilityChange(): void {
    if (document.hidden) {
      if (!this.formStarted) {
        this.exitIntentBeforeEngagement = true;
      }
      this.pushEvent('exit_intent', { trigger: 'tab_hidden', engaged: this.formStarted });
    }
  }

  private _onActivity(): void {
    const now = Date.now();
    const gap = (now - this.lastActivityTime) / 1000;
    if (gap > 30) {
      this.totalIdleTime += gap;
    }
    this.lastActivityTime = now;
  }

  private _checkIdle(): void {
    const idleSeconds = (Date.now() - this.lastActivityTime) / 1000;
    if (idleSeconds >= 30 && idleSeconds < 35) {
      this.pushEvent('idle', { idle_duration_seconds: Math.round(idleSeconds) });
    }
  }

  private pushEvent(type: string, payload: Record<string, unknown>): void {
    if (this.destroyed) return;
    this.eventBuffer.push({
      session_id: this.sessionId,
      event_type: type,
      timestamp: Date.now(),
      payload,
    });
  }

  private flush(): void {
    if (this.eventBuffer.length === 0) return;
    const events = [...this.eventBuffer];
    this.eventBuffer = [];

    // Fire-and-forget via sendBeacon (survives page unload)
    const url = `${this.supabaseUrl}/functions/v1/track-event`;
    const body = JSON.stringify({ events });

    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
    } else {
      // Fallback for older browsers
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': this.anonKey,
        },
        body,
        keepalive: true,
      }).catch(() => {}); // fire-and-forget
    }
  }
}
